import React from "react";
import { Typography, Box, Skeleton } from "@mui/material";
import { useTheme } from "@emotion/react";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    DataGrid,
    GridToolbarContainer,
    GridToolbarColumnsButton,
    GridToolbarFilterButton,
    GridToolbarQuickFilter,
    GridToolbarDensitySelector,
} from "@mui/x-data-grid";
import { URL, origin } from "../config";
import { Line } from "react-chartjs-2";
import Loading from "./Loading";

export default function AccountModal(props) {
    const theme = useTheme();

    const formatter = Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "AUD",
    });

    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
    const [lastUpdated, setlastUpdated] = useState("");
    const [gridData, setGridData] = useState([]);
    const [lineData, setLineData] = useState({});
    const [total, setTotal] = useState(0);

    const gridHeaders = [
        {
            field: "date",
            headerName: "Date of transaction",
            flex: 1,
        },
        {
            field: "amount",
            headerName: "Transaction amount",
            flex: 1,
            type: "number",
            renderCell: (params) => {
                return params.value < 0 ? (
                    <div style={{ color: "red" }}>{params.value}</div>
                ) : (
                    <div style={{ color: "green" }}>{params.value}</div>
                );
            },
        },
        {
            field: "description",
            headerName: "Description",
            flex: 1,
        },
    ];

    const lineOptions = {
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: true,
                labels: {
                    color: theme.palette.primary.contrastText,
                },
            },
            title: {
                display: true,
                text: `${props.account} balance over time`,
                color: theme.palette.primary.contrastText,
            },
        },
        scales: {
            y: {
                grid: {
                    color: theme.palette.primary.contrastText,
                },
                ticks: {
                    color: theme.palette.primary.contrastText,
                },
            },
            x: {
                ticks: {
                    color: theme.palette.primary.contrastText,
                },
            },
        },
    };

    const navigate = useNavigate();

    function fetchData() {
        setLoading(true);
        fetch(`${URL}/fetch/commbank`, {
            method: "GET",
            mode: "cors",
            credentials: "include",
            headers: {
                "content-type": "application/json",
                Origin: `${origin}`,
            },
        })
            .then((response) => {
                if (response.ok !== true) {
                    setError(response.statusText);
                    navigate(`/${response.status}`);
                    throw new Error(response.statusText);
                }
                return response.json();
            })
            .then((data) => {
                console.log(data);
                const numAccounts = data.data.accounts.length;
                for (let i = 0; i < numAccounts; i++) {
                    if (data.data.accounts[i].accountName === props.account) {
                        createDataGridData(data.data.accounts[i].transactions);
                        setLineData(createLineData(data.totalHistory));
                        setTotal(data.data.accounts[i].balance);
                        console.log(data.data.accounts[i].balance);
                    }
                }
                setlastUpdated(
                    new Date(data.lastUpdatedOn).toLocaleString("en-US", {
                        weekday: "long",
                        month: "long",
                        day: "numeric",
                        hour: "numeric",
                        minute: "numeric",
                        hour12: true, // 12-hour time format
                    })
                );
                setLoading(false);
            });
    }

    useEffect(() => {
        fetchData();
    }, []);

    function createDataGridData(data) {
        setGridData(
            data.map((x, index) => {
                const res = {};
                res["id"] = index;
                res["amount"] = x.amount;
                res["date"] = x.purchasedOnDisplay;
                res["description"] = x.description;

                return res;
            })
        );
    }
    function createLineData(data) {
        const totalHistory = data.filter((x) => x.commbank);
        const accountLineData = totalHistory.map((x) => {
            const res = {};
            res["timestamp"] = x.timestamp;
            const valuePair = x.commbank.accounts.filter(
                (x) => x["accountName"] === props.account
            );
            res["value"] = valuePair[0].value;
            return res;
        });

        const result = accountLineData.reduce((a, b) => {
            a[b.timestamp.slice(0, 10)] = b.value;
            return a;
        }, {});

        const graph = {
            labels: Object.keys(result),
            datasets: [
                {
                    label: "Account over time",
                    data: Object.values(result),
                    backgroundColor: "rgba(240, 205, 10, 0.8)",
                    BorderColor: "rgba(240, 205, 10)",
                    color: "white",
                    fill: true,
                },
            ],
        };
        return graph;
    }

    function CustomToolbar() {
        return (
            <GridToolbarContainer>
                <GridToolbarColumnsButton
                    style={{ color: theme.palette.primary.contrastText }}
                />
                <GridToolbarFilterButton
                    style={{ color: theme.palette.primary.contrastText }}
                />
                <GridToolbarDensitySelector
                    style={{ color: theme.palette.primary.contrastText }}
                />
                <GridToolbarQuickFilter
                    style={{ color: theme.palette.primary.contrastText }}
                />
            </GridToolbarContainer>
        );
    }

    return (
        <div hidden={props.value !== props.index}>
            <Box
                display="grid"
                gridTemplateColumns="repeat(12, 1fr)"
                gridAutoRows="100px"
                sx={{
                    "@media (max-width: 75em)": {
                        flexDirection: "column",
                        display: "flex",
                    },
                }}
            >
                <Box
                    gridColumn="span 6"
                    gridRow="span 6"
                    sx={{
                        m: 1,
                        backgroundColor: "primary.main",
                        borderRadius: 2,
                        boxShadow: 14,
                        outline: `1px solid ${theme.palette.primary.main}`,
                        transition: "outline 1s ease",
                        "&:hover": {
                            outline: `1px solid ${theme.palette.primary.contrastText}`,
                        },
                        "@media (max-width: 75em)": {
                            minHeight: "40vh",
                        },
                    }}
                >
                    {loading ? (
                        <Skeleton />
                    ) : (
                        <Line data={lineData} options={lineOptions} />
                    )}
                </Box>
                <Box
                    gridColumn="span 6"
                    gridRow="span 6"
                    sx={{
                        m: 1,
                        backgroundColor: theme.palette.primary.main,
                        borderRadius: 1,
                        outline: `1px solid ${theme.palette.primary.main}`,
                        transition: "outline 1s ease",
                        "&:hover": {
                            outline: `1px solid ${theme.palette.primary.contrastText}`,
                        },
                    }}
                >
                    <DataGrid
                        rows={gridData}
                        columns={gridHeaders}
                        loading={loading}
                        disableRowSelectionOnClick
                        sx={{ boxShadow: 10 }}
                        slots={{ toolbar: CustomToolbar }}
                        slotProps={{
                            toolbar: {
                                showQuickFilter: true,
                            },
                        }}
                    />
                </Box>
                {loading ? (
                    <Loading />
                ) : (
                    <Box gridColumn="span 12">
                        <Typography
                            textAlign={"center"}
                            variant="h5"
                            sx={{ color: theme.palette.primary.contrastText }}
                        >{`${props.account} Balance: ${formatter.format(
                            total
                        )}`}</Typography>
                        <Typography
                            textAlign={"center"}
                            sx={{ color: theme.palette.primary.contrastText }}
                        >{`Last Updated: ${lastUpdated}`}</Typography>
                    </Box>
                )}
            </Box>
        </div>
    );
}
